import React from 'react';
import { Link } from 'react-router-dom';

const LOGO = '/logo.png';
const APP_NAME = 'PlayMaster IA';
const CONTACT_EMAIL = 'playmasteria@gmail.com';
const DOMAIN = 'playmasteria.com';
const LAST_UPDATED = '15 de junio de 2026';

export default function Terms() {
  return (
    <div className="min-h-screen bg-navy text-white">
      <header className="border-b border-gold/20 px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO} alt={APP_NAME} className="h-10" />
          <span className="font-h uppercase tracking-widest text-sm text-gold">{APP_NAME}</span>
        </Link>
        <nav className="text-xs flex gap-4">
          <Link to="/privacy" className="text-gold hover:underline">Privacidad</Link>
          <Link to="/" className="text-gray-400 hover:text-white">Inicio</Link>
        </nav>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="page-terms">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">Legal</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl mb-2">Términos del Servicio</h1>
        <p className="text-xs text-gray-400 mb-8">Última actualización: {LAST_UPDATED}</p>

        <div className="space-y-6 text-sm leading-relaxed text-gray-200">
          <p>
            Bienvenido a <strong>{APP_NAME}</strong> (en adelante, &ldquo;el Servicio&rdquo;). Estos Términos del Servicio
            (los &ldquo;Términos&rdquo;) regulan el acceso y uso de nuestro sitio web, aplicación PWA y APIs disponibles en
            <strong> {DOMAIN}</strong>. Al utilizar el Servicio aceptas estos Términos. Si no estás de acuerdo, no uses el Servicio.
          </p>

          <Section title="1. Definiciones">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Usuario</strong>: cualquier persona que se registra y utiliza el Servicio.</li>
              <li><strong>Coach</strong>: usuario con rol de entrenador que crea jugadas, gestiona rosters y, opcionalmente, publica contenido en el marketplace.</li>
              <li><strong>Jugador</strong>: usuario que se une a un equipo mediante invitación de un coach.</li>
              <li><strong>Contenido del Usuario</strong>: jugadas, animaciones, fotos, descripciones, audios y demás materiales subidos al Servicio.</li>
              <li><strong>Marketplace</strong>: módulo del Servicio donde los coaches pueden publicar y vender jugadas.</li>
            </ul>
          </Section>

          <Section title="2. Cuenta y registro">
            <p>Debes tener al menos 13 años (16 en la UE) para crear una cuenta. Eres responsable de mantener tus credenciales seguras y de toda actividad que ocurra bajo tu cuenta. Notifícanos de inmediato si sospechas un acceso no autorizado.</p>
          </Section>

          <Section title="3. Uso aceptable">
            <p>Te comprometes a no:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Publicar contenido ilegal, ofensivo, discriminatorio, difamatorio o que infrinja derechos de terceros.</li>
              <li>Hacer ingeniería inversa, descompilar o intentar acceder al código fuente no público.</li>
              <li>Sobrecargar nuestros servidores, hacer scraping masivo o eludir mecanismos de seguridad.</li>
              <li>Suplantar a otras personas o entidades.</li>
              <li>Revender, redistribuir o sub-licenciar el Servicio sin autorización por escrito.</li>
            </ul>
            <p className="mt-3">Podemos suspender o terminar tu cuenta si incumples estos términos.</p>
          </Section>

          <Section title="4. Propiedad intelectual">
            <p><strong>Tu contenido</strong>: conservas todos los derechos sobre el Contenido del Usuario que subes. Nos concedes una licencia mundial, no exclusiva, gratuita y transferible para alojar, mostrar, procesar y entregar tu contenido únicamente para operar y mejorar el Servicio (incluida la animación, exportación, traducción multilenguaje, OG-image sharing y narración TTS).</p>
            <p className="mt-3"><strong>Nuestro contenido</strong>: las jugadas pre-cargadas, templates oficiales (Pick & Roll, Horns, Spain PnR, etc.), interfaz, logos y marca son propiedad de <strong>{APP_NAME}</strong> y están protegidos por derechos de autor y marca registrada.</p>
          </Section>

          <Section title="5. Suscripciones y precios">
            <p>Los planes de suscripción se ofrecen mediante pago recurrente mensual o anual:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Coach</strong>: $9.99 USD / mes — editor completo, IA Coach, marketplace.</li>
              <li><strong>Player</strong>: $14.99 USD / mes — funcionalidades avanzadas para jugadores.</li>
              <li><strong>Academy</strong>: $49.99 USD / mes — multi-coach + branding propio.</li>
            </ul>
            <p className="mt-3">Los precios pueden cambiar con un preaviso mínimo de 30 días. Las suscripciones se renuevan automáticamente salvo cancelación antes del próximo ciclo.</p>
          </Section>

          <Section title="6. Pagos y reembolsos">
            <p>Los pagos se procesan vía Stripe. Aceptamos tarjetas de crédito/débito y métodos alternativos disponibles en tu región. Ofrecemos un reembolso íntegro durante los <strong>14 días siguientes</strong> a la primera compra si no estás satisfecho. Para reembolsos, contacta <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
          </Section>

          <Section title="7. Marketplace">
            <p>Los coaches pueden publicar jugadas y fijar el precio ($0.99 – $99.99 USD). Al publicar declaras que el contenido es original o cuentas con todos los derechos necesarios.</p>
            <p className="mt-3">El comprador obtiene una <strong>copia personal</strong> de la jugada para uso propio dentro de la plataforma. No incluye derechos para revenderla.</p>
            <p className="mt-3">Comisión actual de plataforma: <strong>15%</strong> sobre cada venta. El resto se liquida al coach mensualmente vía Stripe Connect (cuando se habilite).</p>
          </Section>

          <Section title="8. Contenido de IA">
            <p>El IA Coach y la generación predictiva utilizan modelos de terceros (Claude, GPT, Gemini). Los resultados son sugerencias automatizadas; pueden contener errores o sesgos. No los uses como única fuente para decisiones críticas. Eres responsable del uso final del contenido generado por IA.</p>
          </Section>

          <Section title="9. Limitación de responsabilidad">
            <p>El Servicio se ofrece &ldquo;tal cual&rdquo; sin garantías de ningún tipo. En la máxima medida permitida por la ley, no seremos responsables de daños indirectos, incidentales, especiales, consecuentes o punitivos derivados del uso o imposibilidad de uso del Servicio. Nuestra responsabilidad agregada no excederá las cantidades pagadas por ti en los <strong>12 meses</strong> previos al incidente.</p>
          </Section>

          <Section title="10. Indemnización">
            <p>Aceptas indemnizar y mantener indemne a {APP_NAME} frente a cualquier reclamación de terceros derivada del Contenido del Usuario que subes, del incumplimiento de estos Términos o de leyes aplicables.</p>
          </Section>

          <Section title="11. Terminación">
            <p>Puedes cancelar tu cuenta en cualquier momento desde la configuración. Podemos suspender o terminar tu cuenta inmediatamente si incumples los Términos. Tras la terminación, tu acceso al Servicio cesa y tus datos se eliminan según nuestra Política de Privacidad.</p>
          </Section>

          <Section title="12. Modificaciones">
            <p>Podemos actualizar estos Términos. Te notificaremos cambios materiales con al menos 7 días de antelación por email o aviso en la app. El uso continuado tras la entrada en vigor constituye aceptación de los nuevos Términos.</p>
          </Section>

          <Section title="13. Ley aplicable y jurisdicción">
            <p>Estos Términos se rigen por la legislación aplicable en el lugar de residencia habitual del usuario en materias de consumo y, en su defecto, por la legislación española. Para conflictos comerciales (B2B) los tribunales competentes serán los de Madrid, España, salvo norma imperativa en contrario.</p>
          </Section>

          <Section title="14. Contacto">
            <p>Email: <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> · Sitio web: <a className="text-gold underline" href={`https://${DOMAIN}`}>https://{DOMAIN}</a>.</p>
          </Section>
        </div>

        <div className="mt-12 pt-6 border-t border-gold/20 text-xs text-gray-400 flex justify-between flex-wrap gap-3">
          <Link to="/privacy" className="text-gold hover:underline">Política de privacidad</Link>
          <Link to="/" className="text-gold hover:underline">Volver al inicio</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-h uppercase text-lg sm:text-xl text-gold mt-6 mb-2">{title}</h2>
      <div className="text-gray-200">{children}</div>
    </section>
  );
}
